import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';

import cx from 'classnames';

//core components
import { useConfirm } from 'material-ui-confirm';
import { Card, CardContent, makeStyles, Divider, Button, Box } from '@material-ui/core';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import VideocamIcon from '@material-ui/icons/Videocam';
import LinkIcon from '@material-ui/icons/Link';
import EventIcon from '@material-ui/icons/Event';
import GetAppIcon from '@material-ui/icons/GetApp';
import FolderIcon from '@material-ui/icons/Folder';
import EmailIcon from '@material-ui/icons/Email';
import DescriptionIcon from '@material-ui/icons/Description';
import SyncIcon from '@material-ui/icons/Sync';
import { Typography } from '@material-ui/core';

//cr components
import GridContainer from 'components/Grid/GridContainer';
import GridItem from 'components/Grid/GridItem';
import PillButton from 'components/PillButton';

//actions & selectors
import {
  openProjectRoleActionDialog,
  updateAssigneeInJob,
  sendContractReminder,
} from 'redux/actions/staffingActions';
import { selectSignedInContractor } from 'redux/selectors/staffing/getContractor';

import { getProjectStatusStyles } from 'utils/staffing/status';
import {
  MEETING_PROVIDERS,
  PROJECT_ERROR_STATUS_STATES,
  PROJECT_STATUS_STATES,
} from 'constants/staffing';

import { includes, size, filter } from 'lodash';

import './openJobsCard.scss';
import { showAppOverlaySpinner } from 'redux/actions/facilitatorSessionActions';
import { enqueueErrorSnackbar, enqueueSuccessSnackbar } from 'redux/actions/notifierActions';
import { useInternalApiClient } from 'hooks/useInternalApiClient';
import Clickable from 'components/Clickable';
import VcEventAttendeeCsvDownloadDialog from 'components/Dialogs/VcEventAttendeeCsvDownloadDialog';

const PROJECT_STATUS_STATES_WITH_CALENDAR = [
  PROJECT_STATUS_STATES.SCHEDULED,
  PROJECT_STATUS_STATES.CREATING_ASSETS,
  PROJECT_ERROR_STATUS_STATES.CREATING_ASSETS,
  PROJECT_STATUS_STATES.IN_PROGRESS,
  PROJECT_STATUS_STATES.READY_TO_INVOICE,
  PROJECT_STATUS_STATES.INVOICING,
  PROJECT_STATUS_STATES.CLOSED,
];

const PROJECT_STATUS_STATES_READY_FOR_INVOICE = [
  PROJECT_STATUS_STATES.INVOICING,
  PROJECT_STATUS_STATES.CLOSED,
];

// useStyles
const useStyles = makeStyles({
  openJobCard: {
    position: 'relative',
  },
  openJobCardDisabled: {
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundColor: 'rgba(0, 0, 0, 0.1)',
      zIndex: 2,
    },
  },
  cardContentDisabled: {
    opacity: 0.5,
    filter: 'grayscale(100%)',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
  },
  cardHeaderTitle: {
    fontSize: '15px',
    lineHeight: '21px',
    letterSpacing: '0.25px',
    fontWeight: '500',
  },
  cardHeaderSubTitle: {
    fontWeight: '300',
    fontSize: '13px',
    lineHeight: '21px',
    letterSpacing: '0.25px',
  },
  cardDescription: {
    fontWeight: '300',
    fontSize: '14px',
    lineHeight: '18px',
    letterSpacing: '0.25px',
    color: '#5E5E5E',
  },
  buttonContainer: {
    display: 'flex',
    gap: '10px',
    marginBottom: 4,
    marginTop: 4,
  },
  controllerButton: {
    fontSize: 11,
    width: '105px',
    height: '34px',
    textTransform: 'uppercase',
    color: '#fff',
  },
});

const ENABLED_STATUSES = ['signed', 'accepted'];

/**
 * Contractor facing job card
 */
const OpenJobCard = ({ job, contractorId }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const confirm = useConfirm();
  const history = useHistory();
  const crApiClient = useInternalApiClient();
  const getContractor = selectSignedInContractor();
  const contractor = useSelector(state => getContractor(state));
  const [isCsvDialogOpen, setIsCsvDialogOpen] = useState(false);

  const toggleCsvDialog = () => {
    setIsCsvDialogOpen(!isCsvDialogOpen);
  };

  //const showFolder = includes(PROJECT_STATUS_STATES_WITH_ASSETS, job.projectStatus)
  const showCal = includes(PROJECT_STATUS_STATES_WITH_CALENDAR, job.projectStatus);
  const showInvoice = includes(PROJECT_STATUS_STATES_READY_FOR_INVOICE, job.projectStatus);

  const user = useSelector(state => state.firebase.profile);
  const isAdmin = user.staffingAdmin;

  const availableSeats =
    size(filter(job.assignees, m => includes(['accepted', 'signed'], m.status))) < job.seats;

  const disableCard = !availableSeats && !includes(ENABLED_STATUSES, job.status);

  const staffType =
    contractor.staffType && contractor.staffType !== 'Employee' ? contractor.staffType : 'Employee';

  const openAcceptRejectDialog = (action, jobId, projectId) => {
    dispatch(openProjectRoleActionDialog(true, action, jobId, projectId));
  };

  const handleSendContract = () => {
    dispatch(
      sendContractReminder(
        job.projectId,
        job.jobId,
        job.contractorEmail,
        job.assigneeInfo.helloSignatureRequestId,
        true,
      ),
    );
  };

  const handleDetailNavigate = () => {
    history.push(`/admin/staffing/projects/detail/${job.projectId}`);
  };

  const reSyncVcEventAttendees = () => {
    confirm({
      description: `This will sync the RSVP count from the Voltage Control website. This also happens automatically every hour.`,
    }).then(async () => {
      dispatch(showAppOverlaySpinner(true));

      try {
        await crApiClient.projects.syncAttendeesFromWp(job.projectId);
        dispatch(enqueueSuccessSnackbar());
      } catch (error) {
        console.error(error);
        dispatch(enqueueErrorSnackbar('Error syncing RSVP count'));
      } finally {
        dispatch(showAppOverlaySpinner(false));
      }
    });
  };

  // Render buttons
  const renderButtons = () => {
    if (!job.isActive) {
      return (
        <>
          {staffType !== 'Employee' &&
            showInvoice && ( //
              <Button
                variant="contained"
                size="small"
                color="primary"
                className={cx(classes.controllerButton)}
                as="a"
                href={`mailto:ap@voltagecontrol.com?subject=Invoice For ${job.eventName}`}
              >
                Send Invoice
              </Button>
            )}
        </>
      );
    }

    switch (job.status) {
      case 'pending':
        if (availableSeats) {
          return (
            <>
              <Button
                className={cx(classes.controllerButton, 'bg-danger')}
                variant="contained"
                size="small"
                onClick={() => {
                  openAcceptRejectDialog('reject', job.jobId, job.projectId);
                }}
              >
                {job.isTentative ? 'No' : 'Reject'}
              </Button>
              <Button
                className={cx(classes.controllerButton, 'bg-success')}
                variant="contained"
                size="small"
                onClick={() => {
                  openAcceptRejectDialog('accept', job.jobId, job.projectId);
                }}
              >
                {job.isTentative ? 'Yes' : 'Accept'}
              </Button>
            </>
          );
        } else {
          return (
            <>
              <Button
                variant="outlined"
                size="small"
                color="default"
                disabled
                className={cx(classes.controllerButton, 'bg-transparent text-secondary')}
              >
                Unavailable
              </Button>
            </>
          );
        }

      case 'rejected':
        return (
          <>
            <Button
              variant="outlined"
              size="small"
              color="default"
              disabled
              className={cx(classes.controllerButton, 'bg-transparent text-secondary')}
            >
              Declined
            </Button>
            <Button
              variant="contained"
              size="small"
              color="primary"
              className={cx(classes.controllerButton)}
              onClick={() => {
                confirm({
                  description:
                    'You are about to undo your rejection of this Job. Are you sure you want to continue?',
                }).then(() => {
                  dispatch(
                    updateAssigneeInJob(job.projectId, job.jobId, contractorId, {
                      status: 'pending',
                    }),
                  );
                });
              }}
            >
              Undo
            </Button>
          </>
        );
      case 'accepted':
        return (
          <>
            <Button
              variant="outlined"
              size="small"
              color="default"
              disabled
              className={cx(classes.controllerButton, 'bg-transparent text-secondary')}
            >
              Accepted
            </Button>
            {staffType !== 'Employee' && job.assigneeInfo.helloSignatureRequestId && (
              <Button
                variant="contained"
                size="small"
                color="primary"
                className={cx(classes.controllerButton, 'bg-success')}
                onClick={handleSendContract}
              >
                Get Contract
              </Button>
            )}
          </>
        );
      case 'signed':
        return (
          <>
            <Button
              variant="outlined"
              size="small"
              color="default"
              disabled
              className={cx(classes.controllerButton, 'bg-transparent text-secondary')}
            >
              Contracted
            </Button>
          </>
        );
      case 'paid':
        return (
          <>
            <Button
              variant="outlined"
              size="small"
              color="default"
              disabled
              className={cx(classes.controllerButton, 'bg-transparent text-secondary')}
            >
              Paid
            </Button>
          </>
        );
      default:
        return <></>;
    }
  };

  const renderJobDescription = () => {
    if (job.status === 'rejected') {
      return job?.assigneeInfo?.notes ?? '';
    }
    return job.isTentative
      ? 'This project date is tentative. Will you hold the date on your calendar for this job?'
      : job.description;
  };

  return (
    <>
      <Card
        className={cx(
          classes.openJobCard,
          'openJobCard',
          disableCard ? classes.openJobCardDisabled : '',
        )}
      >
        <CardContent className={disableCard ? classes.cardContentDisabled : ''}>
          <GridContainer className="w-100 m-0">
            <GridItem xs={12} sm={12} md={12} lg={6}>
              <div className="openJobCard-about">
                <p className="openJobCard-about-title">{job.clientName}</p>
                <p
                  className="openJobCard-about-event"
                  {...(isAdmin
                    ? { onClick: handleDetailNavigate, style: { cursor: 'pointer' } }
                    : {})}
                >
                  {job.eventName}
                </p>
                <p className="openJobCard-about-date mb-0">
                  {job.formattedProjectStartDateLocal} <b>{job.formattedProjectStartTimeLocal}</b> -{' '}
                  {job.formattedProjectEndDateLocal} <b>{job.formattedProjectEndTimeLocal}</b> (
                  {job.formattedProjectTimezone})
                </p>
                <p className="openJobCard-about-date mb-0">
                  {job.projectMethodology} {job.projectServiceType}
                </p>
                {job.projectVCEvent?.attendeesCount !== undefined && (
                  <div className="d-flex align-items-center">
                    <span>RSVP Count: {job.projectVCEvent.attendeesCount}</span>
                    <Clickable
                      onClick={reSyncVcEventAttendees}
                      style={{ marginLeft: 8, padding: 0 }}
                    >
                      <SyncIcon style={{ fontSize: 14 }} />
                    </Clickable>
                    <Clickable onClick={toggleCsvDialog}>
                      <GetAppIcon style={{ fontSize: 14 }} />
                    </Clickable>
                  </div>
                )}
                <div className="d-flex align-items-center mb-0">
                  {job.isTentative && (
                    <div className="openJobCard-about-tentative bg-success">
                      <p>Tentative</p>
                    </div>
                  )}
                </div>
                <div
                  className={cx(
                    'mr-1',
                    'project-card-body-status',
                    getProjectStatusStyles(job.projectStatus).containerClassname,
                  )}
                  style={{ display: 'inline-flex', marginBottom: '10px', marginTop: '10px' }}
                >
                  <Typography className={cx('project-card-body-status-text white-space-no-wrap')}>
                    {getProjectStatusStyles(job.projectStatus).title}
                  </Typography>
                </div>
              </div>
              <div className="openJobCard-about-description">
                <p>{job.projectDescription}</p>
              </div>
            </GridItem>
            <GridItem xs={12} sm={12} md={12} lg={6}>
              <div className="openJobCard-jobDetails">
                <p className="openJobCard-jobDetails-title">Project Details</p>

                <Card className={'openJobsDetailCard'}>
                  <CardContent>
                    <div className={classes.cardHeader}>
                      <div>
                        <p className={cx(classes.cardHeaderTitle, 'mb-0')}>
                          {job.jobName}, {job.jobRole}
                        </p>
                        <p className={cx(classes.cardHeaderSubTitle, 'mb-0')}>
                          {job.seniority}{' '}
                          {contractor?.staffType === 'Contractor' ? job.displayFee : ''}
                        </p>
                      </div>
                      <div className={classes.buttonContainer}>{renderButtons()}</div>
                    </div>

                    <div>
                      <p className={classes.cardDescription}>{renderJobDescription()}</p>
                      <Divider className="my-1" />
                      {(job.status === 'accepted' ||
                        job.status === 'signed' ||
                        job.status === 'paid') &&
                        job.projectVolunteers.length > 0 && (
                          <>
                            {job.projectVolunteers.map(volunteer => (
                              <p className={classes.cardDescription} key={volunteer.id}>
                                <span className="fw-500">{volunteer.role}:</span> {volunteer.name}{' '}
                                <a href={`mailto:${volunteer.email}`}>{volunteer.email}</a>
                              </p>
                            ))}
                          </>
                        )}
                    </div>
                  </CardContent>
                </Card>
                <Box mt={2} display="flex" alignItems="center" flexWrap="wrap">
                  {(job.status === 'accepted' ||
                    job.status === 'signed' ||
                    job.status === 'paid') && (
                    <>
                      <div className="openJobCard-jobDetails">
                        <p className="openJobCard-jobDetails-title">Project Type</p>

                        <div className="d-flex align-items-center flex-wrap">
                          {job.projectIsPublic !== null && (
                            <PillButton
                              type="plainText"
                              text={job.projectIsPublic ? 'Public' : 'Private'}
                              disabled
                            />
                          )}
                          {job.projectIsFree && (
                            <PillButton type="plainText" text="Free" disabled />
                          )}
                          {job.projectVenue && job.projectLocationType !== 'virtual' && (
                            <PillButton
                              type="plainText"
                              text={job.projectVenue}
                              Icon={LocationOnIcon}
                              disabled
                            />
                          )}
                        </div>
                      </div>
                    </>
                  )}

                  {(job.status === 'accepted' ||
                    job.status === 'signed' ||
                    job.status === 'paid') && (
                    <>
                      <div className="openJobCard-jobDetails">
                        <p className="openJobCard-jobDetails-title">Project Links</p>

                        <div className="d-flex align-items-center flex-wrap">
                          {showCal && job.projectCalendarEvent?.webViewLink && (
                            <PillButton
                              type="externalUrl"
                              externalUrl={job.projectCalendarEvent.webViewLink}
                              text="Calendar"
                              Icon={EventIcon}
                            />
                          )}
                          {/* Contractor sees the same URL resolution as admin (Meet + legacy fallback). */}
                          {(job.projectZoomLink || job.projectMeetingLink) && (
                            <PillButton
                              type="externalUrl"
                              externalUrl={
                                job.projectMeetingProvider === MEETING_PROVIDERS.googleMeet
                                  ? job.projectMeetingLink || job.projectZoomLink
                                  : job.projectZoomLink
                              }
                              text={
                                job.projectMeetingProvider === MEETING_PROVIDERS.googleMeet
                                  ? 'Meet Link'
                                  : 'Zoom Link'
                              }
                              Icon={VideocamIcon}
                            />
                          )}
                          {(job.status === 'signed' || job.status === 'paid') &&
                            job.projectProjectBrief?.webViewLink && (
                              <PillButton
                                type="externalUrl"
                                externalUrl={job.projectProjectBrief.webViewLink}
                                text="Project Brief"
                                Icon={LinkIcon}
                              />
                            )}
                          {(job.status === 'signed' || job.status === 'paid') &&
                            job.projectProjectFolder?.webViewLink && (
                              <PillButton
                                type="externalUrl"
                                externalUrl={job.projectProjectFolder.webViewLink}
                                text="Folder"
                                Icon={FolderIcon}
                              />
                            )}
                          {(job.status === 'signed' || job.status === 'paid') && (
                            <PillButton
                              type="clickToCopy"
                              clickToCopyContent={`${process.env.REACT_APP_HOSTING_ENV === 'prod' ? 'projects' : 'projectsdev'}+${job.projectId}@voltagecontrol.com`}
                              copySuccessMessage="Email Copied"
                              text="Email"
                              Icon={EmailIcon}
                            />
                          )}
                          {(job.status === 'signed' || job.status === 'paid') && (
                            <PillButton
                              type="clickToCopy"
                              clickToCopyContent={`${process.env.REACT_APP_HOSTING_ENV === 'prod' ? 'notetaker' : 'notetakerdev'}+${job.projectId}@voltagecontrol.com`}
                              copySuccessMessage="Email Copied"
                              text="Notetaker"
                              Icon={DescriptionIcon}
                            />
                          )}
                          {(job.status === 'signed' || job.status === 'paid') &&
                            job.projectRunOfShowDoc?.webViewLink && (
                              <PillButton
                                type="externalUrl"
                                externalUrl={job.projectRunOfShowDoc.webViewLink}
                                text="ROS"
                                Icon={LinkIcon}
                              />
                            )}
                          {(job.status === 'signed' || job.status === 'paid') &&
                            job.projectChecklistDoc?.webViewLink && (
                              <PillButton
                                type="externalUrl"
                                externalUrl={job.projectChecklistDoc.webViewLink}
                                text="Checklist"
                                Icon={LinkIcon}
                              />
                            )}
                          {(job.status === 'signed' || job.status === 'paid') &&
                            job.projectVCEvent?.webViewLink && (
                              <PillButton
                                type="externalUrl"
                                externalUrl={job.projectVCEvent.webViewLink}
                                text="Event RSVP"
                                Icon={LinkIcon}
                              />
                            )}
                          {job.projectVolunteerRoles &&
                            Object.keys(job.projectVolunteerRoles).length > 0 && (
                              <PillButton
                                type="externalUrl"
                                externalUrl={`${process.env.REACT_APP_URL}/volunteer-signup/${job.projectId}`}
                                text="Volunteer Signup"
                                Icon={LinkIcon}
                              />
                            )}
                        </div>
                      </div>
                    </>
                  )}
                </Box>
              </div>
            </GridItem>
          </GridContainer>
        </CardContent>
      </Card>
      <VcEventAttendeeCsvDownloadDialog
        isCsvDialogOpen={isCsvDialogOpen}
        toggleCsvDialog={toggleCsvDialog}
        eventId={job.projectVCEvent?.id}
        eventName={job.eventName}
      />
    </>
  );
};

OpenJobCard.propTypes = {
  job: PropTypes.object.isRequired,
  contractorId: PropTypes.string.isRequired,
};

export default OpenJobCard;
